import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { User } from 'firebase/auth';
import { authService } from '@/services/firebase/auth';

type AuthState = {
  user: User | null;
  loading: boolean;
  error: Error | null;
};

type AuthContextType = AuthState & {
  signIn: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithMicrosoft: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  isAuthenticated: boolean;
};

const AuthContext = createContext<AuthContextType | null>(null);

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });
  const [redirectProcessed, setRedirectProcessed] = useState(false);

  useEffect(() => {
    let mounted = true;
    let authStateResolved = false;
    let redirectUser: User | null = null;

    console.log('[Auth] useEffect starting, setting up auth...');

    // Process any pending redirect result first
    const processRedirect = async () => {
      console.log('[Auth] processRedirect: calling getRedirectResult...');
      try {
        redirectUser = await authService.getRedirectResult();
        console.log('[Auth] processRedirect: getRedirectResult returned:', redirectUser?.email || 'null');
        if (redirectUser && mounted) {
          console.log('[Auth] processRedirect: Setting user state from redirect');
          setState({ user: redirectUser, loading: false, error: null });
        }
      } catch (error) {
        console.error('[Auth] processRedirect: Error:', error);
        if (mounted) {
          setState((prev) => ({ ...prev, error: error as Error }));
        }
      }

      // If we got a user from redirect, wait for onAuthStateChanged to confirm
      // Otherwise, wait for onAuthStateChanged to tell us the auth state
      if (redirectUser) {
        console.log('[Auth] processRedirect: Got redirect user, waiting 100ms for sync...');
        await new Promise(resolve => setTimeout(resolve, 100));
      } else {
        console.log('[Auth] processRedirect: No redirect user, waiting for authStateResolved...');
        const maxWait = 2000;
        const start = Date.now();
        while (mounted && !authStateResolved && Date.now() - start < maxWait) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        console.log('[Auth] processRedirect: Wait complete. authStateResolved:', authStateResolved);
      }

      if (mounted) {
        console.log('[Auth] processRedirect: Setting redirectProcessed = true');
        setRedirectProcessed(true);
      }
    };

    // Set up auth state listener FIRST
    const unsubscribe = authService.onAuthStateChanged(async (user) => {
      console.log('[Auth] onAuthStateChanged fired:', user?.email || 'null');
      authStateResolved = true;

      // Ensure user has a profile in Firestore (creates on first sign-in, updates lastLoginAt)
      if (user) {
        try {
          await authService.ensureUserProfile(user);
          console.log('[Auth] User profile ensured for:', user.email);
        } catch (err) {
          console.error('[Auth] Failed to ensure user profile:', err);
        }
      }

      if (mounted) {
        console.log('[Auth] onAuthStateChanged: Setting state with user:', user?.email || 'null');
        setState({ user, loading: false, error: null });
      }
    });

    // Then process redirect
    processRedirect();

    return () => {
      console.log('[Auth] useEffect cleanup');
      mounted = false;
      unsubscribe();
    };
  }, []);

  // Keep showing loading until redirect is processed (prevents flash of login page)
  const isLoading = state.loading || !redirectProcessed;

  const signInWithGoogle = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      // Uses redirect flow - page will navigate away, then onAuthStateChanged
      // will update state when user returns after successful auth
      await authService.signInWithGoogle();
    } catch (error) {
      setState((prev) => ({ ...prev, error: error as Error, loading: false }));
      throw error;
    }
  };

  const signInWithMicrosoft = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      // Uses redirect flow - page will navigate away, then onAuthStateChanged
      // will update state when user returns after successful auth
      await authService.signInWithMicrosoft();
    } catch (error) {
      setState((prev) => ({ ...prev, error: error as Error, loading: false }));
      throw error;
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      await authService.signInWithEmail(email, password);
      // onAuthStateChanged will update the state
    } catch (error) {
      setState((prev) => ({ ...prev, error: error as Error, loading: false }));
      throw error;
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      await authService.signUpWithEmail(email, password);
      // onAuthStateChanged will update the state
    } catch (error) {
      setState((prev) => ({ ...prev, error: error as Error, loading: false }));
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await authService.resetPassword(email);
    } catch (error) {
      setState((prev) => ({ ...prev, error: error as Error }));
      throw error;
    }
  };

  // Default signIn uses Google for backwards compatibility
  const signIn = signInWithGoogle;

  const signOut = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true }));
      await authService.signOut();
    } catch (error) {
      setState((prev) => ({ ...prev, error: error as Error, loading: false }));
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      user: state.user,
      loading: isLoading,
      error: state.error,
      signIn,
      signInWithGoogle,
      signInWithMicrosoft,
      signInWithEmail,
      signUpWithEmail,
      resetPassword,
      signOut,
      isAuthenticated: !!state.user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
